/**
 * remoteProjectileSceneManager — sync thin meshes from MP snapshot.projectiles.
 *
 * Presentation only — no combat formulas. Prefer ability VFX when vfxId present.
 *
 * Contrato:
 *   createRemoteProjectileMesh(wire, THREE) → mesh|null
 *   syncRemoteProjectileMeshes(scene, wires, meshById, THREE) → Map
 */

import { createAbilityVfxMesh } from '../vfx/abilityVfx.js';

const TEAM_COLOR = { blue: 0x38bdf8, red: 0xf87171 };
const Y_OFFSET = 1.1;

/**
 * @param {object} wire
 * @param {object} THREE
 * @returns {object|null}
 */
export function createRemoteProjectileMesh(wire, THREE) {
  if (!wire?.id || !THREE) return null;
  const fromVfx = createAbilityVfxMesh(wire.vfxId, THREE);
  const mesh = fromVfx || buildFallbackSphere(wire, THREE);
  if (!mesh) return null;
  mesh.name = `remote-proj-${wire.id}`;
  mesh.userData = { ...(mesh.userData || {}), remoteProjectileId: wire.id };
  placeMesh(mesh, wire);
  return mesh;
}

/**
 * @param {object} scene
 * @param {object[]} wires
 * @param {Map<string, object>} meshById
 * @param {object} THREE
 * @returns {Map<string, object>}
 */
export function syncRemoteProjectileMeshes(scene, wires, meshById, THREE) {
  if (!scene || !meshById || !THREE) return meshById || new Map();
  const list = Array.isArray(wires) ? wires : [];
  const keep = new Set();

  for (const wire of list) {
    if (!wire?.id) continue;
    keep.add(wire.id);
    let mesh = meshById.get(wire.id);
    if (!mesh) {
      mesh = createRemoteProjectileMesh(wire, THREE);
      if (!mesh) continue;
      meshById.set(wire.id, mesh);
      if (typeof scene.add === 'function') scene.add(mesh);
    } else {
      placeMesh(mesh, wire);
    }
  }

  for (const [id, mesh] of [...meshById.entries()]) {
    if (keep.has(id)) continue;
    meshById.delete(id);
    if (typeof scene.remove === 'function') scene.remove(mesh);
  }
  return meshById;
}

function placeMesh(mesh, wire) {
  const x = Number.isFinite(wire.x) ? wire.x : 0;
  const z = Number.isFinite(wire.z) ? wire.z : 0;
  if (mesh.position?.set) mesh.position.set(x, Y_OFFSET, z);
  if (Number.isFinite(wire.angle) && mesh.rotation) {
    mesh.rotation.y = wire.angle;
  }
}

function buildFallbackSphere(wire, THREE) {
  if (!THREE.Mesh || !THREE.SphereGeometry || !THREE.MeshBasicMaterial) return null;
  const team = wire.team === 'red' || wire.team === 'chaos' ? 'red' : 'blue';
  return new THREE.Mesh(
    new THREE.SphereGeometry(0.28, 8, 8),
    new THREE.MeshBasicMaterial({
      color: TEAM_COLOR[team],
      transparent: true,
      opacity: 0.9
    })
  );
}
